import { z } from 'zod';
import { parseDto } from '../../mappers/parseDto';
import type { Transport } from '../../transport/Transport';

const summaryDto = z.object({
  id: z.string(), slug: z.string(), title: z.string(),
  image_url: z.string().nullable(), share_image_url: z.string().nullable(),
  campaign_label: z.string().nullable(), savings_badge_format: z.enum(['amount', 'percent']),
  featured: z.boolean(), product_count: z.number(),
  price: z.number(), compare_at_price: z.number(), savings: z.number(), savings_percent: z.number(),
  available_quantity: z.number(),
});
const itemDto = z.object({
  id: z.string(), slug: z.string(), title: z.string(), sku: z.string().nullable(),
  image_url: z.string().nullable(), quantity: z.number(),
  regular_price: z.number(), selling_price: z.number(),
  available_quantity: z.number(),
  specifications: z.array(z.object({ name: z.string(), value: z.string() })),
});
const detailDto = summaryDto.extend({
  description: z.string(), short_description: z.string(),
  meta_title: z.string(), meta_description: z.string(),
  products: z.array(itemDto),
  quote: z.object({
    regular_total: z.number(), individual_total: z.number(),
    collection_price: z.number(), savings: z.number(), savings_percent: z.number(), available_quantity: z.number(),
    selected_products: z.array(z.number()),
  }),
  categories: z.array(z.object({ slug: z.string(), name: z.string() })),
  selection_mode: z.enum(['fixed', 'choice']),
  choice_groups: z.array(z.object({ name: z.string(), product_ids: z.array(z.number()) })),
  tier_two_percent: z.number(), tier_three_percent: z.number(),
});
const quoteDto = z.object({
  regular_total: z.number(), individual_total: z.number(),
  collection_price: z.number(), savings: z.number(), savings_percent: z.number(), available_quantity: z.number(),
  selected_products: z.array(z.number()),
});

export type CollectionSummary = z.infer<typeof summaryDto>;
export type CollectionDetail = z.infer<typeof detailDto>;

export interface CollectionService {
  list(categorySlug: string, signal?: AbortSignal): Promise<CollectionSummary[]>;
  featured(signal?: AbortSignal): Promise<CollectionSummary[]>;
  get(slug: string, signal?: AbortSignal): Promise<CollectionDetail>;
  track(slug: string, event: 'view' | 'add_to_cart'): Promise<void>;
  quote(slug: string, selectedProducts: number[], quantity?: number): Promise<z.infer<typeof quoteDto>>;
}

export function createLaravelCollectionService(transport: Transport): CollectionService {
  return {
    async list(categorySlug, signal) {
      const response = await transport.request({
        method: 'GET', path: '/collections', query: { category: categorySlug },
        ...(signal ? { signal } : {}),
      });
      return parseDto(z.object({ data: z.array(summaryDto) }), response.data, 'GET /collections').data;
    },
    async featured(signal) {
      const response = await transport.request({
        method: 'GET', path: '/collections', query: { featured: '1' },
        ...(signal ? { signal } : {}),
      });
      return parseDto(z.object({ data: z.array(summaryDto) }), response.data, 'GET /collections?featured=1').data;
    },
    async get(slug, signal) {
      const response = await transport.request({
        method: 'GET', path: '/collections/' + encodeURIComponent(slug),
        ...(signal ? { signal } : {}),
      });
      return parseDto(z.object({ data: detailDto }), response.data, 'GET /collections/{slug}').data;
    },
    async track(slug, event) {
      await transport.request({
        method: 'POST', path: '/collections/' + encodeURIComponent(slug) + '/events',
        body: { event },
      });
    },
    async quote(slug, selectedProducts, quantity = 1) {
      const response = await transport.request({
        method: 'POST', path: '/collections/' + encodeURIComponent(slug) + '/quote',
        body: { selected_products: selectedProducts, quantity },
      });
      return parseDto(z.object({ data: quoteDto }), response.data, 'POST /collections/{slug}/quote').data;
    },
  };
}
